// Thin typed client for the backend's REST surface (src/api/server.py + src/api/admin.py).
// Every call carries a Bearer token; the server resolves identity from it —
// the frontend never invents/impersonates a user_id.

const BASE_URL = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(
  path: string,
  token: string,
  init: RequestInit = {},
): Promise<T> {
  const resp = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...init.headers,
    },
  });

  if (!resp.ok) {
    const message = await extractErrorMessage(resp);
    throw new ApiError(resp.status, message);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

async function extractErrorMessage(resp: Response): Promise<string> {
  try {
    const body = await resp.json();
    if (typeof body.detail === "string") return body.detail;
    if (Array.isArray(body.detail)) {
      // FastAPI/pydantic 422 校验错误：[{loc, msg, type}, ...]
      return body.detail.map((d: { msg?: string }) => d.msg).join("; ");
    }
    return JSON.stringify(body);
  } catch {
    return `HTTP ${resp.status}`;
  }
}

// ---------- Chat ----------

export interface ChatResponse {
  thread_id: string;
  status: "completed" | "interrupted";
  reply?: string;
  question?: string;
}

export function sendChat(
  token: string,
  message: string,
  threadId?: string,
  env?: string,
): Promise<ChatResponse> {
  return request<ChatResponse>("/v1/chat", token, {
    method: "POST",
    body: JSON.stringify({ message, thread_id: threadId, env }),
  });
}

export function resumeChat(
  token: string,
  threadId: string,
  answer: string,
): Promise<ChatResponse> {
  return request<ChatResponse>("/v1/chat/resume", token, {
    method: "POST",
    body: JSON.stringify({ thread_id: threadId, answer }),
  });
}

// ---------- Excel export download ----------

/**
 * Download an agent-generated export (path like "/v1/exports/{file_id}").
 * Plain <a href> can't carry the Bearer token, so we fetch the blob with
 * auth headers and trigger a client-side save.
 */
export async function downloadExport(token: string, path: string): Promise<void> {
  const resp = await fetch(`${BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    throw new ApiError(resp.status, await extractErrorMessage(resp));
  }

  // 文件名优先取 Content-Disposition 的 filename*（后端对中文名做了 RFC 5987 编码）
  const disposition = resp.headers.get("Content-Disposition") ?? "";
  const starMatch = disposition.match(/filename\*=utf-8''([^;]+)/i);
  const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
  const filename = starMatch
    ? decodeURIComponent(starMatch[1])
    : plainMatch?.[1] ?? "export.xlsx";

  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------- Identity ----------

export interface MeResponse {
  user_id: string;
  name: string;
}

export function fetchMe(token: string): Promise<MeResponse> {
  return request<MeResponse>("/v1/me", token);
}

// ---------- Environments ----------

export interface EnvironmentInfo {
  name: string;
  description: string;
  aliases: string[];
}

export interface EnvironmentsResponse {
  default_env: string;
  environments: EnvironmentInfo[];
}

export function fetchEnvironments(token: string): Promise<EnvironmentsResponse> {
  return request<EnvironmentsResponse>("/v1/environments", token);
}

// ---------- Models & preferences ----------

export interface ModelInfo {
  key: string;
  display_name: string;
}

export interface ModelsResponse {
  default_diagnostic_model: string;
  default_classifier_model: string;
  models: ModelInfo[];
}

// 用户自带的模型连接（"bring your own model"）；写入前后端会做一次真实连通性测试
export interface CustomModelInput {
  base_url: string;
  model_name: string;
  api_key: string;
  display_name?: string;
}

// 自定义连接的脱敏展示：api_key 只回显尾 4 位
export interface CustomModelPublic {
  base_url: string;
  model_name: string;
  display_name: string;
  api_key_hint: string;
}

export interface ModelSelection {
  source: "catalog" | "custom";
  model_key: string | null;
  custom: CustomModelPublic | null;
}

export interface Preferences {
  diagnostic: ModelSelection;
  classifier: ModelSelection;
}

export interface PreferencesUpdate {
  diagnostic_model?: string;
  diagnostic_custom?: CustomModelInput;
  classifier_model?: string;
  classifier_custom?: CustomModelInput;
}

export function fetchModels(token: string): Promise<ModelsResponse> {
  return request<ModelsResponse>("/v1/models", token);
}

export function fetchPreferences(token: string): Promise<Preferences> {
  return request<Preferences>("/v1/me/preferences", token);
}

export function updatePreferences(
  token: string,
  updates: PreferencesUpdate,
): Promise<Preferences> {
  return request<Preferences>("/v1/me/preferences", token, {
    method: "PUT",
    body: JSON.stringify(updates),
  });
}

// ---------- Admin (key management) ----------

export interface AdminUser {
  user_id: string;
  name: string;
}

export interface AdminCreateUserResponse extends AdminUser {
  api_key: string;
}

export function adminListUsers(adminSecret: string): Promise<AdminUser[]> {
  return request<AdminUser[]>("/v1/admin/users", adminSecret);
}

export function adminCreateUser(
  adminSecret: string,
  userId: string,
  name: string,
): Promise<AdminCreateUserResponse> {
  return request<AdminCreateUserResponse>("/v1/admin/users", adminSecret, {
    method: "POST",
    body: JSON.stringify({ user_id: userId, name }),
  });
}

export function adminRevokeUser(
  adminSecret: string,
  userId: string,
): Promise<{ revoked: boolean }> {
  return request<{ revoked: boolean }>(
    `/v1/admin/users/${encodeURIComponent(userId)}`,
    adminSecret,
    { method: "DELETE" },
  );
}

// ---------- Admin (Telegram channel) ----------

export interface TelegramSubject {
  tg_id: number;
  note: string;
}

export interface TelegramBotModels {
  diagnostic_model: string | null; // null = 使用目录默认
  classifier_model: string | null;
  default_diagnostic_model: string;
  default_classifier_model: string;
  available_models: ModelInfo[];
}

// kind: "users"（授权用户）| "groups"（授权群组）
export type TelegramSubjectKind = "users" | "groups";

export function adminTgListSubjects(
  adminSecret: string,
  kind: TelegramSubjectKind,
): Promise<TelegramSubject[]> {
  return request<TelegramSubject[]>(`/v1/admin/telegram/${kind}`, adminSecret);
}

export function adminTgAddSubject(
  adminSecret: string,
  kind: TelegramSubjectKind,
  tgId: number,
  note: string,
): Promise<TelegramSubject> {
  return request<TelegramSubject>(`/v1/admin/telegram/${kind}`, adminSecret, {
    method: "POST",
    body: JSON.stringify({ tg_id: tgId, note }),
  });
}

export function adminTgRemoveSubject(
  adminSecret: string,
  kind: TelegramSubjectKind,
  tgId: number,
): Promise<{ revoked: boolean }> {
  return request<{ revoked: boolean }>(
    `/v1/admin/telegram/${kind}/${tgId}`,
    adminSecret,
    { method: "DELETE" },
  );
}

export function adminTgGetBotModels(adminSecret: string): Promise<TelegramBotModels> {
  return request<TelegramBotModels>("/v1/admin/telegram/models", adminSecret);
}

// 传空字符串 "" 表示恢复目录默认；undefined 表示不修改该角色
export function adminTgSetBotModels(
  adminSecret: string,
  updates: { diagnostic_model?: string; classifier_model?: string },
): Promise<TelegramBotModels> {
  return request<TelegramBotModels>("/v1/admin/telegram/models", adminSecret, {
    method: "PUT",
    body: JSON.stringify(updates),
  });
}
